import {
  DollarSign,
  Package,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface KpiCardsProps {
  data: any;
}

export function DashboardCard({ data }: KpiCardsProps) {
  const { totalRevenue, orderStatusDistribution } = data;

  const totalOrders = orderStatusDistribution.reduce(
    (sum: number, item: any) => sum + item.count,
    0,
  );
  const ownedOrders =
    orderStatusDistribution.find((item: any) => item.status === 'OWNED')
      ?.count || 0;
  const createdOrders =
    orderStatusDistribution.find((item: any) => item.status === 'CREATED')
      ?.count || 0;
  const avgOrderValue = totalRevenue.totalRevenue / totalOrders;

  // Mock previous period data
  const previousPeriodData = {
    totalRevenue: 283,
    totalOrders: 28,
    totalQuantity: 29,
    avgOrderValue: 10.11,
  };

  const calculateTrend = (current: number, previous: number) => {
    if (previous === 0) return { value: 100, direction: 'up' };
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      direction: change >= 0 ? 'up' : 'down',
    };
  };

  const kpis = [
    {
      title: 'Total Revenue',
      value: `$${totalRevenue.totalRevenue}`,
      trend: calculateTrend(
        totalRevenue.totalRevenue,
        previousPeriodData.totalRevenue,
      ),
      icon: DollarSign,
      description: `${ownedOrders} completed orders`,
    },
    {
      title: 'Total Orders',
      value: totalOrders.toString(),
      trend: calculateTrend(totalOrders, previousPeriodData.totalOrders),
      icon: ShoppingCart,
      description: `${createdOrders} pending orders`,
    },
    {
      title: 'Products Sold',
      value: totalRevenue.totalQuantity.toString(),
      trend: calculateTrend(
        totalRevenue.totalQuantity,
        previousPeriodData.totalQuantity,
      ),
      icon: Package,
      description: 'Total units sold',
    },
    {
      title: 'Avg. Order Value',
      value: `$${avgOrderValue.toFixed(2)}`,
      trend: calculateTrend(avgOrderValue, previousPeriodData.avgOrderValue),
      icon: TrendingUp,
      description: 'Per order average',
    },
  ];

  const getTrendColor = (direction: 'up' | 'down') =>
    direction === 'up' ? 'text-green-600' : 'text-red-600';

  const getTrendBgColor = (direction: 'up' | 'down') =>
    direction === 'up'
      ? 'bg-green-50 border-green-200'
      : 'bg-red-50 border-red-200';

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {kpis.map((kpi, index) => (
        <Card
          key={index}
          className={`border-l-4 ${
            kpi.trend.direction === 'up'
              ? 'border-l-green-500'
              : 'border-l-red-500'
          } hover:shadow-lg transition-all duration-200`}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{kpi.title}</CardTitle>
            <div
              className={`p-2 rounded-full ${getTrendBgColor(kpi.trend.direction as 'up' | 'down')}`}
            >
              {kpi.trend.direction === 'up' ? (
                <TrendingUp className={`h-4 w-4 ${getTrendColor('up')}`} />
              ) : (
                <TrendingDown className={`h-4 w-4 ${getTrendColor('down')}`} />
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpi.value}</div>
            <div className="flex items-center space-x-1 mt-1">
              <span
                className={`text-sm font-medium ${getTrendColor(kpi.trend.direction as 'up' | 'down')}`}
              >
                {kpi.trend.direction === 'up' ? '+' : '-'}
                {kpi.trend.value.toFixed(1)}%
              </span>
              <span className="text-xs text-muted-foreground">vs previous</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {kpi.description}
            </p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
export default DashboardCard;
